/**
 * ReceiptPrintDialog: the order's receipt document, in the same window.
 * ───────────────────────────────────────────────────────────────────────────
 * Mounted once in App.js and opened through utilities/receiptDialogBus.js by
 * openReceiptForPrint(orderId). Printing used to open the signed receipt page
 * in a new tab, which took the operator off the order they were handling and
 * left a tab behind for every receipt (17 Oct 2026). The document now loads in
 * a frame inside this dialog; Print prints the frame, not the dashboard page.
 *
 * The frame's page is the server's own receipt (controllers/receiptController
 * viewSignedReceipt), so what prints here is byte for byte what a customer
 * receives. Print asks the frame to print by postMessage: the receipt page
 * listens for "lusuku-receipt:print", which works whether the frame is on this
 * origin (production, lusuku.shop) or on the API host during development,
 * where calling print() on the frame directly is refused. "Open in new tab"
 * stays for anyone who wants the document on its own.
 *
 * The same dialog shows Lusuku's own e-invoice and credit note (the Tax menu's
 * Fiscal documents page): the request may carry a `title`, and those pages
 * answer the same print message.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Modal, Spinner } from "react-bootstrap";
import { subscribeReceiptDialog } from "../../utilities/receiptDialogBus";

const PRINT_MESSAGE = "lusuku-receipt:print";
const FILLED = { backgroundColor: "#083860", borderColor: "#083860", color: "#fff", fontWeight: 600 };

export default function ReceiptPrintDialog() {
  const [request, setRequest] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const frameRef = useRef(null);

  useEffect(
    () =>
      subscribeReceiptDialog((next) => {
        setLoaded(false);
        setRequest(next && next.viewUrl ? next : null);
      }),
    [],
  );

  const close = useCallback(() => setRequest(null), []);

  const print = useCallback(() => {
    const frame = frameRef.current;
    if (!frame || !request?.viewUrl) return;
    try {
      // Same origin: print the frame directly, no round trip.
      frame.contentWindow.focus();
      frame.contentWindow.print();
    } catch (_) {
      // Another origin: the receipt page prints itself on this message.
      frame.contentWindow.postMessage(PRINT_MESSAGE, new URL(request.viewUrl).origin);
    }
  }, [request]);

  if (!request) return null;

  return (
    <Modal show onHide={close} size="lg" centered backdrop="static" dialogClassName="lk-receipt-dialog">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "var(--lk-text-md, 15px)", fontWeight: 700 }}>{request.title || "Receipt"}</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ padding: 0, background: "#f4f6f9", position: "relative", minHeight: 420 }}>
        {!loaded && (
          <div
            style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}
          >
            <Spinner animation="border" size="sm" role="status" aria-label={`Loading ${request.title || "receipt"}`} />
          </div>
        )}
        <iframe
          ref={frameRef}
          title={request.title || "Receipt"}
          src={request.viewUrl}
          onLoad={() => setLoaded(true)}
          style={{ display: "block", width: "100%", height: "70vh", border: 0, opacity: loaded ? 1 : 0 }}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={close}>
          Close
        </Button>
        <Button variant="light" as="a" href={request.viewUrl} target="_blank" rel="noopener noreferrer">
          Open in new tab
        </Button>
        <Button style={FILLED} onClick={print} disabled={!loaded}>
          Print
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
